import { useCallback } from 'react';

import { WorkspaceDescriptor } from '@synapex/shared2/entities';

import { QueryFnData } from '../../engine';
import { AdvertiserAccount, Selector } from '../entity';
import { all } from './key-fn';
import { useAll } from './query';

type AllReply = QueryFnData<typeof all>;

export const useActiveUser = () => {
  const { data: currentUser } = useAll({
    select: Selector.activeUser,
  });

  return currentUser;
};

export const useDefaultAccount = () => {
  const { data } = useAll({
    select: Selector.defaultAccount,
  });

  return data;
};

export const useAccountById = (accountId: number) => {
  const select = useCallback(
    (reply: AllReply) => Selector.accountById(accountId, reply),
    [accountId],
  );

  const { data: account } = useAll({
    select,
  });

  return account;
};

export const useAccounts = () => {
  const { data: accounts } = useAll({
    select: Selector.accounts,
  });

  return accounts;
};

export const useNewAccountChannelRequests = () => {
  const { data: accounts } = useAll({
    select: Selector.newAccountChannelRequests,
  });

  return accounts;
};

//workspace
export const useDefaultWorkspace = (account: AdvertiserAccount | null) => {
  const select = useCallback(
    (reply: AllReply) => Selector.defaultWorkspace(account, reply),
    [account],
  );

  const { data: defaultWorkspace } = useAll({
    select,
  });

  return defaultWorkspace;
};

export const useWorkspaceById = (
  account: AdvertiserAccount | null,
  { workspace_type, workspace_id }: WorkspaceDescriptor,
) => {
  const select = useCallback(
    (reply: AllReply) =>
      Selector.workspaceById(account, { workspace_type, workspace_id }, reply),
    [workspace_type, workspace_id, account],
  );

  const { data: workspace } = useAll({
    select,
  });

  return workspace;
};

export const useAccountWorkspaces = (accountId: number | null) => {
  const select = useCallback(
    (reply: AllReply) => Selector.accountWorkspaces(accountId, reply),
    [accountId],
  );

  const { data: accountWorkspaces } = useAll({
    select,
  });

  return accountWorkspaces;
};

export const useAccountChannelRequests = (accountId: number | null) => {
  const select = useCallback(
    (reply: AllReply) => Selector.accountChannelRequests(accountId, reply),
    [accountId],
  );

  const { data: crs } = useAll({
    select,
  });

  return crs;
};

//account and workspace
export const useDefaultAccountAndWorkspace = () => {
  const { data: defaultAccountAndWorkspace } = useAll({
    select: Selector.defaultAccountAndWorkspace,
  });

  return defaultAccountAndWorkspace;
};

export const usePathParamsEntitiesById = ({
  advertiser_account_id,
  workspace_type,
  workspace_id,
  telegram_channel_id,
}: {
  advertiser_account_id?: number;
  telegram_channel_id?: string;
} & Partial<WorkspaceDescriptor>) => {
  const select = useCallback(
    (reply: AllReply) =>
      Selector.pathParamsEntitiesById(
        {
          advertiser_account_id,
          workspace_type,
          workspace_id,
          telegram_channel_id,
        },
        reply,
      ),
    [advertiser_account_id, workspace_type, workspace_id, telegram_channel_id],
  );

  const { data: defaultAccountAndWorkspace } = useAll({
    select,
    enabled: Boolean(
      telegram_channel_id ||
        advertiser_account_id ||
        (workspace_type && workspace_id),
    ),
  });

  return defaultAccountAndWorkspace;
};
